// apps/web/src/features/notifications/components/NotificationDot.tsx
//
// @aiPurpose row-level notification dot (#1072). Distinct from chat
//   `UnreadBadge`: signals presence of any unread aggregate (rate /
//   rejection / insurance / responsibleStatus / chat) on a request.
//   Pure-presentational — the caller passes the aggregate count.
// @aiInvariant uses the design-token `--status-warn-fg` (amber) to
//   visually separate from the chat unread badge (red `--status-error-fg`)
//   so a row carrying both shows two distinct dots, not one merged
//   indicator.
import { useTranslation } from 'react-i18next';

const NOTIFICATION_DISPLAY_CAP = 99;

export interface NotificationDotProps {
  count: number;
  // INVARIANT: rendered only when count > 0; callers should not pass
  //   zero, but the component returns null defensively.
}

export function NotificationDot({ count }: NotificationDotProps): JSX.Element | null {
  const { t } = useTranslation('shell');
  if (count <= 0) return null;
  if (count === 1) {
    return (
      <span
        role="status"
        aria-label={t('notificationsRow.aria.dot')}
        data-testid="notification-row-dot"
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 'var(--r-full)',
          background: 'var(--status-warn-fg)',
          flexShrink: 0,
        }}
      />
    );
  }
  const capped = count > NOTIFICATION_DISPLAY_CAP;
  const display = capped ? `${NOTIFICATION_DISPLAY_CAP}+` : String(count);
  const ariaLabel = capped
    ? t('notificationsRow.aria.countCapped', { count: NOTIFICATION_DISPLAY_CAP })
    : t('notificationsRow.aria.count', { count });
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      data-testid="notification-row-count"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 6px',
        borderRadius: 'var(--r-full)',
        background: 'var(--status-warn-fg)',
        color: 'var(--fg-invert)',
        fontSize: 'var(--fz-11)',
        fontWeight: 600,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}
    >
      {display}
    </span>
  );
}
